import React, { useState, useEffect, useRef, useMemo } from "react"
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import { useSelector, useDispatch } from "react-redux"
import { getCabs } from "../../../../store/Cab/CabAction"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { toast } from "react-toastify"
import { getPlaceDetailsByPlaceId } from "../../../../utils/placeDetails"
import { isSameLocation } from "../../../../utils/locationValidation"
import { getDefaultOptions, getDefaultOptionsSync } from "../../../../utils/defaultCityOptions"
import { saveRecentSearch } from "../../../../utils/recentSearches"
import { slugify } from "../../../../utils/slugify"
import { DatePickerInput, TimePickerInput, DateTimeProvider } from "../../../../components/DateTime"
import { useMapsReady } from "../../../../hooks/useMapsReady"

const formatShortDate = (dateStr) => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
}

const formatShortTime = (timeStr) => {
  if (!timeStr) return null
  const [h, m] = timeStr.split(":").map(Number)
  if (isNaN(h)) return timeStr
  const ampm = h >= 12 ? "PM" : "AM"
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`
}

const truncate = (str, n = 18) => (str && str.length > n ? str.slice(0, n) + "…" : str)

const panelPlacesStyles = {
  container: (p) => ({ ...p, width: "100%" }),
  control: (provided) => ({
    ...provided,
    border: "1.5px solid #e0e0e0",
    boxShadow: "none",
    width: "100%",
    height: "44px",
    minHeight: "44px",
    background: "#fff",
    fontSize: "1.3rem",
    color: "#333",
    borderRadius: "0.6rem",
  }),
  valueContainer: (p) => ({ ...p, height: "44px", padding: "0 10px" }),
  input: (p) => ({ ...p, color: "#333", fontSize: "1.3rem", margin: 0, padding: 0 }),
  placeholder: (p) => ({ ...p, color: "#aaa", fontSize: "1.3rem" }),
  singleValue: (p) => ({ ...p, color: "#333" }),
  menu: (p) => ({ ...p, fontSize: "1.3rem", zIndex: 99999 }),
}

const UpdateOutstationForm = () => {
  const [formValues, setFormValues] = useState({
    type: "one way",
    from: null,
    to: null,
    pickUpDate: "",
    pickUpTime: "",
    returnDate: "",
    returnTime: "",
  })

  const { location } = useSelector((state) => state.location)
  const [showReturn, setShowReturn] = useState(false)
  const [mobileExpanded, setMobileExpanded] = useState(false)
  const mapsReady = useMapsReady()
  const [defaultFromOptions, setDefaultFromOptions] = useState(() =>
    getDefaultOptionsSync("from")
  )
  const [defaultToOptions, setDefaultToOptions] = useState(() =>
    getDefaultOptionsSync("to")
  )
  const [fromMenuIsOpen, setFromMenuIsOpen] = useState(false)
  const [toMenuIsOpen, setToMenuIsOpen] = useState(false)

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)
  const returnDateRef = useRef(null)
  const returnTimeRef = useRef(null)

  const params = useParams()
  const location_path = useLocation()
  const today = new Date()
  const formattedToday = today.toLocaleDateString("en-CA")
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchLoading, setSearchLoading] = useState(false)
  const submitLockRef = useRef(false)

  const loadFormValues = () => {
    const storedTripData = localStorage.getItem("tripData")
    if (!storedTripData) return

    const tripData = JSON.parse(storedTripData)

    const convertStringToPlaceFormat = (stringValue, lat, lng, city) => {
      if (!stringValue) return null
      if (typeof stringValue === "string") {
        return {
          label: stringValue,
          value: { description: stringValue, place_id: `temp_${Date.now()}_${Math.random()}` },
          latitude: lat || null,
          longitude: lng || null,
          city: city || "",
        }
      } else if (stringValue?.label && stringValue?.value) {
        return stringValue
      }
      return null
    }

    setFormValues({
      type: tripData.round_trip ? "round trip" : "one way",
      from: tripData.pickup ? convertStringToPlaceFormat(tripData.pickup, tripData.pickup_lat, tripData.pickup_long, tripData.pickup_city) : null,
      to: tripData.drop ? convertStringToPlaceFormat(tripData.drop, tripData.drop_lat, tripData.drop_long, tripData.drop_city) : null,
      pickUpDate: tripData.trip_date || "",
      pickUpTime: tripData.trip_time || "",
      returnDate: tripData.return_date || "",
      returnTime: tripData.return_time || "",
    })
    setShowReturn(tripData.round_trip === 1)
  }

  useEffect(() => {
    loadFormValues()
    const handleStorageChange = () => loadFormValues()
    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("tripDataUpdated", handleStorageChange)
    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("tripDataUpdated", handleStorageChange)
    }
  }, [params.fromCity, params.toCity, params.routePath, location_path.pathname])

  useEffect(() => {
    const loadDefaultOptions = async () => {
      const currentCity = location?.city || formValues?.from?.city || formValues?.from?.label
      const fromCity = formValues?.from?.city || formValues?.from?.label
      const fromOptions = await getDefaultOptions("from", currentCity)
      const toOptions = await getDefaultOptions("to", null, fromCity || currentCity)
      setDefaultFromOptions(fromOptions)
      setDefaultToOptions(toOptions)
    }
    loadDefaultOptions()
  }, [location?.city, formValues?.from])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === "type") {
      const isRound = value === "round trip"
      setShowReturn(isRound)
      setFormValues((prev) => ({
        ...prev,
        type: value,
        ...(!isRound && { returnDate: "", returnTime: "" }),
      }))
      return
    }
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formValues.from) return toast.error("Please select a pickup location")
    if (!formValues.to) return toast.error("Please select a drop location")
    if (isSameLocation(formValues.from, formValues.to)) return toast.error("Pickup and drop locations cannot be the same")
    if (!formValues.pickUpDate) return toast.error("Please select pickup date")
    if (!formValues.pickUpTime) return toast.error("Please select pickup time")

    if (formValues.type === "round trip") {
      if (!formValues.returnDate) return toast.error("Please select return date")
      if (!formValues.returnTime) return toast.error("Please select return time")
    }

    if (submitLockRef.current || searchLoading) return
    submitLockRef.current = true
    setSearchLoading(true)

    const parsedTripData = JSON.parse(localStorage.getItem("tripData") || "{}")
    const getFullAddress = (v) => (v ? (typeof v === "string" ? v : v.fullAddress || v.label || "") : "")
    const extractCityFromText = (text = "") => (text.split(",")[0] || "").trim()

    const pickupCityForRoute =
      formValues.from?.city ||
      parsedTripData.pickup_city_for_route ||
      parsedTripData.pickup_city ||
      extractCityFromText(getFullAddress(formValues.from)) ||
      ""

    const dropCityForRoute =
      formValues.to?.city ||
      parsedTripData.drop_city_for_route ||
      parsedTripData.drop_city ||
      extractCityFromText(getFullAddress(formValues.to)) ||
      ""

    const tripDataForApi = {
      trip_type: 3,
      round_trip: formValues.type === "round trip" ? 1 : 0,
      pickup: getFullAddress(formValues.from) || parsedTripData.pickup || "",
      drop: getFullAddress(formValues.to) || parsedTripData.drop || "",
      pickup_lat: formValues.from?.latitude || parsedTripData.pickup_lat || null,
      pickup_long: formValues.from?.longitude || parsedTripData.pickup_long || null,
      drop_lat: formValues.to?.latitude || parsedTripData.drop_lat || null,
      drop_long: formValues.to?.longitude || parsedTripData.drop_long || null,

      // IMPORTANT: empty city fields force default 11/km fare calculation.
      pickup_city: "",
      drop_city: "",

      package_type_in_hours: "",
      trip_date: formValues.pickUpDate,
      trip_time: formValues.pickUpTime,
      phone_number: parsedTripData.phone_number || "",
      ip_address: location?.ip || parsedTripData.ip_address || "8425",
      return_date: formValues.returnDate,
      return_time: formValues.returnTime,
    }

    const tripDataForStorage = {
      ...tripDataForApi,
      pickup_city: pickupCityForRoute,
      drop_city: dropCityForRoute,
      pickup_city_for_route: pickupCityForRoute,
      drop_city_for_route: dropCityForRoute,
    }

    const safetyTimeout = setTimeout(() => {
      submitLockRef.current = false
      setSearchLoading(false)
    }, 15000)

    try {
      localStorage.removeItem("cabSearchResult")
      const response = await dispatch(getCabs(tripDataForApi)).unwrap()

      localStorage.setItem("tripData", JSON.stringify(tripDataForStorage))
      localStorage.setItem("cabSearchResult", JSON.stringify(response?.data ?? {}))
      window.dispatchEvent(new Event("tripDataUpdated"))

      const from = slugify(pickupCityForRoute)
      const to = slugify(dropCityForRoute)

      setMobileExpanded(false)
      navigate(`/routes/${from}-to-${to}-cab/`, { state: { tripData: tripDataForStorage } })
    } catch (err) {
      const msg = err && typeof err === "string" ? err : err?.message || "Something went wrong"
      const isRateLimit =
        typeof msg === "string" &&
        (msg.includes("Rate limit") || msg.includes("Maximum"))

      toast.error(
        isRateLimit
          ? "You've exceeded the request limit. Please try again after a few hours."
          : msg
      )
    } finally {
      clearTimeout(safetyTimeout)
      submitLockRef.current = false
      setSearchLoading(false)
    }
  }

  const handlePlaceSelect = async (value, field) => {
    if (!value) {
      setFormValues((prev) => ({ ...prev, [field]: null }))
      return
    }
    if (field === "to" && formValues.from && isSameLocation(value, formValues.from)) {
      setFormValues((prev) => ({ ...prev, to: null }))
      const storedTrip = JSON.parse(localStorage.getItem("tripData") || "{}")
      delete storedTrip.drop; delete storedTrip.drop_lat; delete storedTrip.drop_long; delete storedTrip.drop_city
      localStorage.setItem("tripData", JSON.stringify(storedTrip))
      toast.error("Pickup and drop locations cannot be the same")
      return
    }
    try {
      const placeId = value?.value?.place_id || value?.place_id
      let details = null
      if (placeId && !placeId.startsWith("temp_")) {
        details = await getPlaceDetailsByPlaceId(placeId)
      } else if (value?.label) {
        details = { lat: value.latitude || null, lng: value.longitude || null, city: value.city || value.label, fullAddress: value?.value?.description || value.label }
      }
      if (details) {
        const nextValue = { ...value, latitude: details.lat, longitude: details.lng, city: details.city, fullAddress: details.fullAddress }
        setFormValues((prev) => ({ ...prev, [field]: nextValue }))
        saveRecentSearch(nextValue, field)
        if (field === "from") {
          const toOptions = await getDefaultOptions("to", null, details.city)
          setDefaultToOptions(toOptions)
          setToMenuIsOpen(true)
        } else {
          setTimeout(() => { pickUpDateRef.current?.open?.() }, 150)
        }
      }
    } catch (error) {
      console.error("Error fetching location details:", error)
    }
  }

  const fromLabel = formValues.from?.label || "—"
  const toLabel = formValues.to?.label || "—"
  const pickupDateShort = formatShortDate(formValues.pickUpDate)
  const pickupTimeShort = formatShortTime(formValues.pickUpTime)
  const returnDateShort = formatShortDate(formValues.returnDate)
  const returnTimeShort = formatShortTime(formValues.returnTime)
  const isRoundTrip = formValues.type === "round trip"
  const tripTypeChip = isRoundTrip ? "Round Trip" : "One Way"

  // Same-day round-trip return is allowed (see OutstationForm for the full
  // reasoning): the return-date floor is the pickup day itself, and a small
  // buffer is only a same-day TIME floor so the return stays after pickup.
  const MIN_RETURN_GAP_HOURS = 1

  const pickupDateTime = useMemo(() => {
    if (!formValues.pickUpDate || !formValues.pickUpTime) return null
    const [h, m] = formValues.pickUpTime.split(":").map(Number)
    if (isNaN(h)) return null
    const d = new Date(formValues.pickUpDate)
    d.setHours(h, m || 0, 0, 0)
    return d
  }, [formValues.pickUpDate, formValues.pickUpTime])

  const earliestReturn = useMemo(
    () => (pickupDateTime ? new Date(pickupDateTime.getTime() + MIN_RETURN_GAP_HOURS * 60 * 60 * 1000) : null),
    [pickupDateTime]
  )

  const returnMinDate = formValues.pickUpDate
    ? new Date(formValues.pickUpDate)
    : new Date(formattedToday)

  const sameCalendarDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

  const returnMinTime = pickupDateTime && earliestReturn && formValues.returnDate
    ? (() => {
        const retDate = new Date(formValues.returnDate)
        if (!sameCalendarDay(retDate, pickupDateTime)) return null
        if (!sameCalendarDay(earliestReturn, pickupDateTime)) return null
        const rh = earliestReturn.getHours()
        const rm = earliestReturn.getMinutes()
        const rounded = rm > 0 ? Math.ceil(rm / 30) * 30 : 0
        const finalH = rounded >= 60 ? rh + 1 : rh
        const finalM = rounded >= 60 ? 0 : rounded
        if (finalH >= 24) return null
        return `${String(finalH).padStart(2, "0")}:${String(finalM).padStart(2, "0")}`
      })()
    : null

  useEffect(() => {
    if (!pickupDateTime || !formValues.returnDate) return
    const retDate = new Date(formValues.returnDate)
    const retDateOnly = new Date(retDate.getFullYear(), retDate.getMonth(), retDate.getDate())
    const pickDateOnly = new Date(pickupDateTime.getFullYear(), pickupDateTime.getMonth(), pickupDateTime.getDate())
    if (retDateOnly < pickDateOnly) {
      setFormValues((prev) => ({ ...prev, returnDate: "", returnTime: "" }))
      return
    }
    if (
      formValues.returnTime &&
      retDateOnly.getTime() === pickDateOnly.getTime() &&
      sameCalendarDay(earliestReturn, pickupDateTime)
    ) {
      const [rh, rm] = formValues.returnTime.split(":").map(Number)
      const retMinutes = rh * 60 + (rm || 0)
      const minMinutes = earliestReturn.getHours() * 60 + earliestReturn.getMinutes()
      if (retMinutes < minMinutes) {
        setFormValues((prev) => ({ ...prev, returnTime: "" }))
      }
    }
  }, [pickupDateTime, earliestReturn, formValues.returnDate, formValues.returnTime])

  const placesComponents = { DropdownIndicator: () => null, IndicatorSeparator: () => null, ClearIndicator: () => null }

  return (
    <div className="updateOutstationForm">
      {/* ── BAR (always visible) ── */}
      <div className="updateOutstationForm__bar">
        <span className="updateOutstationForm__bar-badge">{tripTypeChip}</span>

        <div className="updateOutstationForm__bar-chip">
          <span className="updateOutstationForm__bar-chipLabel">From</span>
          <span className="updateOutstationForm__bar-chipValue">{truncate(fromLabel, 20)}</span>
        </div>

        <span className="updateOutstationForm__bar-arrow">
          {formValues.type === "round trip" ? "⇄" : "→"}
        </span>

        <div className="updateOutstationForm__bar-chip">
          <span className="updateOutstationForm__bar-chipLabel">To</span>
          <span className="updateOutstationForm__bar-chipValue">{truncate(toLabel, 20)}</span>
        </div>

        <div className="updateOutstationForm__bar-chip">
          <span className="updateOutstationForm__bar-chipLabel">{isRoundTrip ? "Pickup" : "Date"}</span>
          <span className="updateOutstationForm__bar-chipValue">{pickupDateShort || "—"}</span>
        </div>

        <div className="updateOutstationForm__bar-chip updateOutstationForm__bar-chip--time">
          <span className="updateOutstationForm__bar-chipLabel">{isRoundTrip ? "At" : "Time"}</span>
          <span className="updateOutstationForm__bar-chipValue">{pickupTimeShort || "—"}</span>
        </div>

        {isRoundTrip && (
          <>
            <div className="updateOutstationForm__bar-chip">
              <span className="updateOutstationForm__bar-chipLabel">Return</span>
              <span className="updateOutstationForm__bar-chipValue">{returnDateShort || "—"}</span>
            </div>

            <div className="updateOutstationForm__bar-chip updateOutstationForm__bar-chip--time">
              <span className="updateOutstationForm__bar-chipLabel">At</span>
              <span className="updateOutstationForm__bar-chipValue">{returnTimeShort || "—"}</span>
            </div>
          </>
        )}

        <button
          className="updateOutstationForm__bar-modifyBtn"
          onClick={() => setMobileExpanded((v) => !v)}
          type="button"
        >
          {mobileExpanded ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Close
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Modify
            </>
          )}
        </button>
      </div>

      {/* ── PANEL (slides down on Modify) ── */}
      <div className={`updateOutstationForm__panel${mobileExpanded ? " updateOutstationForm__panel--open" : ""}`}>
        <form
          onSubmit={handleSubmit}
          className={
            formValues.type === "one way"
              ? "updateOutstationForm__row updateOutstationForm__row--one"
              : "updateOutstationForm__row updateOutstationForm__row--round"
          }
        >
          {/* TRIP TYPE */}
          <div className="field field--type">
            <label className="field-label">Trip Type</label>
            <select name="type" value={formValues.type} onChange={handleChange} className="update-input-select">
              <option value="one way">One Way</option>
              <option value="round trip">Round Trip</option>
            </select>
          </div>

          {/* FROM */}
          <div className="field field--from">
            <label className="field-label">From</label>
            {mapsReady && <GooglePlacesAutocomplete
              key={`from-${defaultFromOptions.map((o) => o.label).join("|")}`}
              debounce={400}
              minLengthAutocomplete={3}
              selectProps={{
                value: formValues.from || null,
                onChange: (value) => { handlePlaceSelect(value, "from"); setFromMenuIsOpen(false) },
                placeholder: "From *",
                isClearable: true,
                defaultOptions: defaultFromOptions,
                menuIsOpen: fromMenuIsOpen,
                onFocus: () => setFromMenuIsOpen(true),
                onBlur: () => setFromMenuIsOpen(false),
                onMenuClose: () => setFromMenuIsOpen(false),
                getOptionLabel: (e) => (e && e.label) || "",
                getOptionValue: (e) => e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",
                components: placesComponents,
                styles: panelPlacesStyles,
              }}
              autocompletionRequest={{ componentRestrictions: { country: "in" } }}
            />}
          </div>

          {/* TO */}
          <div className="field field--to">
            <label className="field-label">To</label>
            {mapsReady && <GooglePlacesAutocomplete
              key={`to-${defaultToOptions.map((o) => o.label).join("|")}`}
              debounce={400}
              minLengthAutocomplete={3}
              selectProps={{
                value: formValues.to || null,
                onChange: (value) => { handlePlaceSelect(value, "to"); setToMenuIsOpen(false) },
                placeholder: "To *",
                isClearable: true,
                defaultOptions: defaultToOptions,
                menuIsOpen: toMenuIsOpen,
                onFocus: () => setToMenuIsOpen(true),
                onBlur: () => setToMenuIsOpen(false),
                onMenuClose: () => setToMenuIsOpen(false),
                getOptionLabel: (e) => (e && e.label) || "",
                getOptionValue: (e) => e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",
                components: placesComponents,
                styles: panelPlacesStyles,
              }}
              autocompletionRequest={{ componentRestrictions: { country: "in" } }}
            />}
          </div>

          <DateTimeProvider>
            {/* PICKUP DATE */}
            <div className="field field--pdate dtp-white">
              <label className="field-label">Pickup Date</label>
              <DatePickerInput
                ref={pickUpDateRef}
                value={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
                minDate={new Date(formattedToday)}
                placeholder="Pick Up Date"
                onChange={(d) => {
                  const newDate = d ? new Date(d).toLocaleDateString("en-CA") : ""
                  setFormValues((prev) => {
                    const updated = { ...prev, pickUpDate: newDate, pickUpTime: "" }
                    if (prev.returnDate && newDate > prev.returnDate) {
                      updated.returnDate = ""
                      updated.returnTime = ""
                    }
                    return updated
                  })
                  setTimeout(() => { pickUpTimeRef.current?.open?.() }, 200)
                }}
              />
            </div>

            {/* PICKUP TIME */}
            <div className="field field--ptime ttp-white">
              <label className="field-label">Pickup Time</label>
              <TimePickerInput
                ref={pickUpTimeRef}
                value={formValues.pickUpTime}
                placeholder="Pick Up Time"
                selectedDate={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
                onChange={(t) => {
                  setFormValues((prev) => ({ ...prev, pickUpTime: t || "" }))
                  if (showReturn) setTimeout(() => { returnDateRef.current?.open?.() }, 200)
                }}
              />
            </div>

            {/* RETURN DATE / TIME */}
            {showReturn && (
              <>
                <div className="field field--rdate dtp-white">
                  <label className="field-label">Return Date</label>
                  <DatePickerInput
                    ref={returnDateRef}
                    value={formValues.returnDate ? new Date(formValues.returnDate) : null}
                    minDate={returnMinDate}
                    placeholder="Return Date"
                    onChange={(d) => {
                      const newReturn = d ? new Date(d).toLocaleDateString("en-CA") : ""
                      setFormValues((prev) => {
                        const updated = { ...prev, returnDate: newReturn }
                        if (newReturn !== prev.returnDate) updated.returnTime = ""
                        return updated
                      })
                      setTimeout(() => { returnTimeRef.current?.open?.() }, 200)
                    }}
                  />
                </div>
                <div className="field field--rtime ttp-white">
                  <label className="field-label">Return Time</label>
                  <TimePickerInput
                    ref={returnTimeRef}
                    value={formValues.returnTime}
                    placeholder="Return Time"
                    selectedDate={formValues.returnDate ? new Date(formValues.returnDate) : null}
                    minTime={returnMinTime}
                    onChange={(t) => setFormValues((prev) => ({ ...prev, returnTime: t || "" }))}
                  />
                </div>
              </>
            )}
          </DateTimeProvider>

          {/* SUBMIT */}
          <div className="field field--submit">
            <label className="field-label">&nbsp;</label>
            <button className="update-submit-btn" type="submit" disabled={searchLoading}>
              {searchLoading ? "Searching..." : "Search"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UpdateOutstationForm
